import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const STATUSES = ["registration_open", "coming_soon", "closed", "completed"];
const CATEGORIES = ["MARATHON", "HALF MARATHON", "10K", "5K", "FUN RUN", "TRAIL RUN", "DESTINATION RUN", "RUNSAFARI"];
const REGIONS = ["EAST", "NORTH EAST", "NORTH", "CENTRAL", "SOUTH", "WEST"];

const EMPTY = {
  name: "", slug: "", category: "MARATHON", status: "coming_soon", featured: false, published: true,
  date_text: "", event_date: "", year: "", state: "", region: "EAST", city: "", destination: "", venue: "",
  landscapes: "", distances: "", entry_fee: "", entry_fee_amount: 0, description: "", route_info: "", prizes: "",
  tshirt: "Event T-Shirt", medal: "Finisher Medal", refreshments: "", hydration: "", accommodation: "",
  travel_info: "", contact: "", registration_mode: "closed", registration_link: "", image: "", video: "",
  benefits: "", rules: "", sponsors: "", gallery: "", schedule: "", faqs: "", results: "",
};

const toLines = (arr, joiner = " | ") => (arr || []).map((x) => (typeof x === "object" ? Object.values(x).join(joiner) : x)).join("\n");
const parsePairs = (text, keys) => (text || "").split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
  const parts = l.split("|").map((p) => p.trim());
  const obj = {};
  keys.forEach((k, i) => { obj[k] = parts[i] ?? ""; });
  if (keys.includes("position")) obj.position = Number(obj.position) || "";
  return obj;
});
const parseList = (text) => (text || "").split("\n").map((l) => l.trim()).filter(Boolean);
const parseCsv = (text) => (text || "").split(",").map((l) => l.trim()).filter(Boolean);

function Field({ label, k, form, set, type = "text", textarea = false, placeholder = "", testid }) {
  return (
    <label className="block">
      <span className="mb-1.5 block font-mono text-[10px] tracking-[0.2em] text-slate-500">{label}</span>
      {textarea ? (
        <textarea data-testid={testid || `ef-${k}`} rows={3} placeholder={placeholder} className="input-k" value={form[k]} onChange={(e) => set(k, e.target.value)} />
      ) : (
        <input data-testid={testid || `ef-${k}`} type={type} placeholder={placeholder} className="input-k" value={form[k]} onChange={(e) => set(k, e.target.value)} />
      )}
    </label>
  );
}

export default function EventForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (initial) {
      setForm({
        ...EMPTY, ...initial,
        landscapes: (initial.landscapes || []).join(", "),
        distances: (initial.distances || []).join(", "),
        benefits: toLines(initial.benefits),
        rules: toLines(initial.rules),
        sponsors: toLines(initial.sponsors),
        gallery: toLines(initial.gallery),
        schedule: toLines(initial.schedule),
        faqs: toLines(initial.faqs),
        results: toLines(initial.results),
      });
    }
  }, [initial]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      ...form,
      entry_fee_amount: Number(form.entry_fee_amount) || 0,
      landscapes: parseCsv(form.landscapes).map((s) => s.toUpperCase()),
      distances: parseCsv(form.distances).map((s) => s.toUpperCase()),
      benefits: parseList(form.benefits),
      rules: parseList(form.rules),
      sponsors: parseList(form.sponsors),
      gallery: parseList(form.gallery),
      schedule: parsePairs(form.schedule, ["time", "activity"]),
      faqs: parsePairs(form.faqs, ["q", "a"]),
      results: parsePairs(form.results, ["bib", "name", "category", "time", "position"]),
    };
    try {
      const saved = initial?.id
        ? (await api.put(`/admin/events/${initial.id}`, payload)).data
        : (await api.post("/admin/events", payload)).data;
      toast.success(initial?.id ? "Event updated" : "Event created");
      onSaved(saved);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="event-form-modal" className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto p-4">
      <div className="fixed inset-0 bg-brand-ink/85 backdrop-blur-sm" onClick={onClose} />
      <div className="card-k relative z-10 my-8 w-full max-w-4xl p-7">
        <button data-testid="event-form-close" onClick={onClose} className="absolute right-4 top-4 text-slate-400 hover:text-white"><X size={20} /></button>
        <h2 className="font-display text-3xl font-bold uppercase text-white">{initial?.id ? "Edit Event" : "Add New Event"}</h2>
        <form onSubmit={submit} className="mt-7 space-y-7">
          <div>
            <p className="eyebrow mb-3">BASICS</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="EVENT NAME *" k="name" form={form} set={set} />
              <Field label="SLUG (auto if empty)" k="slug" form={form} set={set} placeholder="sundarban-tiger-marathon" />
              <label className="block"><span className="mb-1.5 block font-mono text-[10px] tracking-[0.2em] text-slate-500">CATEGORY</span>
                <select data-testid="ef-category" className="input-k" value={form.category} onChange={(e) => set("category", e.target.value)}>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></label>
              <label className="block"><span className="mb-1.5 block font-mono text-[10px] tracking-[0.2em] text-slate-500">STATUS</span>
                <select data-testid="ef-status" className="input-k" value={form.status} onChange={(e) => set("status", e.target.value)}>{STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ").toUpperCase()}</option>)}</select></label>
              <Field label="DATE (DISPLAY) *" k="date_text" form={form} set={set} placeholder="4 October 2026" />
              <Field label="EVENT DATE (ISO)" k="event_date" form={form} set={set} type="date" />
              <Field label="YEAR" k="year" form={form} set={set} placeholder="2026" />
              <Field label="IMAGE URL" k="image" form={form} set={set} placeholder="https://..." />
              <Field label="EVENT VIDEO URL (optional)" k="video" form={form} set={set} placeholder="https://...mp4" />
            </div>
            <div className="mt-4 flex flex-wrap gap-6">
              <label className="flex items-center gap-2 text-sm text-slate-300"><input data-testid="ef-featured" type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="accent-[#18A8DD]" /> Featured project</label>
              <label className="flex items-center gap-2 text-sm text-slate-300"><input data-testid="ef-published" type="checkbox" checked={form.published} onChange={(e) => set("published", e.target.checked)} className="accent-[#9BAE1B]" /> Published (visible on site)</label>
            </div>
          </div>

          <div>
            <p className="eyebrow mb-3">LOCATION</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="DESTINATION *" k="destination" form={form} set={set} placeholder="Jharkhali, Sundarban" />
              <Field label="VENUE" k="venue" form={form} set={set} />
              <Field label="CITY" k="city" form={form} set={set} />
              <Field label="STATE *" k="state" form={form} set={set} placeholder="West Bengal" />
              <label className="block"><span className="mb-1.5 block font-mono text-[10px] tracking-[0.2em] text-slate-500">REGION</span>
                <select data-testid="ef-region" className="input-k" value={form.region} onChange={(e) => set("region", e.target.value)}>{REGIONS.map((r) => <option key={r}>{r}</option>)}</select></label>
              <Field label="LANDSCAPES (comma separated)" k="landscapes" form={form} set={set} placeholder="FOREST, BEACH, HILL, VILLAGE, HERITAGE, TEA" />
            </div>
          </div>

          <div>
            <p className="eyebrow mb-3">RACE DETAILS</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="DISTANCES (comma separated)" k="distances" form={form} set={set} placeholder="42K, 21K, 10K, 5K" />
              <Field label="ENTRY FEE (DISPLAY TEXT)" k="entry_fee" form={form} set={set} placeholder="Rs 800 onwards" />
              <Field label="ENTRY FEE AMOUNT (INR — 0 = free registration)" k="entry_fee_amount" form={form} set={set} type="number" placeholder="800" />
            </div>
            <div className="mt-4 grid gap-4">
              <Field label="DESCRIPTION" k="description" form={form} set={set} textarea />
              <Field label="ROUTE INFORMATION" k="route_info" form={form} set={set} textarea />
            </div>
          </div>

          <div>
            <p className="eyebrow mb-3">REGISTRATION</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block"><span className="mb-1.5 block font-mono text-[10px] tracking-[0.2em] text-slate-500">MODE</span>
                <select data-testid="ef-reg-mode" className="input-k" value={form.registration_mode} onChange={(e) => set("registration_mode", e.target.value)}>
                  <option value="onsite">On-site form (this website)</option>
                  <option value="external">External link</option>
                  <option value="closed">Closed / not available</option>
                </select></label>
              <Field label="EXTERNAL REGISTRATION LINK" k="registration_link" form={form} set={set} placeholder="https://..." />
            </div>
          </div>

          <div>
            <p className="eyebrow mb-3">EXTRAS (one per line; schedule/faqs/results use " | " separators)</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="BENEFITS" k="benefits" form={form} set={set} textarea placeholder={"Finisher Medal\nEvent T-Shirt"} />
              <Field label="RULES" k="rules" form={form} set={set} textarea />
              <Field label="SCHEDULE (time | activity)" k="schedule" form={form} set={set} textarea placeholder={"05:30 | 21K flag-off"} />
              <Field label="FAQS (question | answer)" k="faqs" form={form} set={set} textarea />
              <Field label="RESULTS (bib | name | category | time | position)" k="results" form={form} set={set} textarea placeholder={"STM-101 | Arjun Pradhan | 21K Men | 1:24:36 | 1"} />
              <Field label="SPONSORS" k="sponsors" form={form} set={set} textarea />
              <Field label="GALLERY IMAGE URLS" k="gallery" form={form} set={set} textarea />
            </div>
          </div>

          <div>
            <p className="eyebrow mb-3">HOSPITALITY & CONTACT</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="PRIZES" k="prizes" form={form} set={set} />
              <Field label="T-SHIRT" k="tshirt" form={form} set={set} />
              <Field label="MEDAL" k="medal" form={form} set={set} />
              <Field label="REFRESHMENTS" k="refreshments" form={form} set={set} />
              <Field label="HYDRATION" k="hydration" form={form} set={set} />
              <Field label="ACCOMMODATION" k="accommodation" form={form} set={set} />
              <Field label="TRAVEL INFO" k="travel_info" form={form} set={set} />
              <Field label="EVENT CONTACT" k="contact" form={form} set={set} />
            </div>
          </div>

          <button data-testid="event-form-save" disabled={busy} className="btn-kinetic btn-primary-k w-full justify-center disabled:opacity-60">
            {busy ? "Saving..." : initial?.id ? "SAVE CHANGES" : "CREATE EVENT"}
          </button>
        </form>
      </div>
    </div>
  );
}
